import React from "react";
import "./App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth, homeForRole } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { CartProvider } from "./context/CartContext";
import { LocationProvider } from "./context/LocationContext";
import MarketingLayout from "./components/marketing/MarketingLayout";
import AppLayout from "./components/app/AppLayout";
import Landing from "./pages/public/Landing";
import { ForRiders, ForDrivers, ForRestaurants } from "./pages/public/RolePages";
import About from "./pages/public/About";
import Contact from "./pages/public/Contact";
import Stories from "./pages/public/Stories";
import StoryDetail from "./pages/public/StoryDetail";
import { Terms, Privacy, Refunds, Grievance, DeleteAccountInfo, Charges, Partners } from "./pages/public/Legal";
import Download from "./pages/public/Download";
import TripShare from "./pages/public/TripShare";
import PrivateCars from "./pages/customer/PrivateCars";
import DriversNearMe from "./pages/customer/DriversNearMe";
import PrivateCarJobs from "./pages/driver/PrivateCarJobs";
import Login from "./pages/auth/Login";
import Signup from "./pages/auth/Signup";
import PhoneAuth from "./pages/auth/PhoneAuth";
import VerifyPhone from "./pages/auth/VerifyPhone";
import SignupVerify from "./pages/auth/SignupVerify";
import ForgotPassword from "./pages/auth/ForgotPassword";
import BookRide from "./pages/customer/BookRide";
import GoodsDelivery from "./pages/customer/GoodsDelivery";
import LiveTrip from "./pages/customer/LiveTrip";
import Food from "./pages/customer/Food";
import RestaurantDetail from "./pages/customer/RestaurantDetail";
import Checkout from "./pages/customer/Checkout";
import Orders from "./pages/customer/Orders";
import OrderDetail from "./pages/customer/OrderDetail";
import RideHistory from "./pages/customer/RideHistory";
import Chat from "./pages/shared/Chat";
import Profile from "./pages/shared/Profile";
import KycStatus from "./pages/shared/KycStatus";
import Schedule from "./pages/shared/Schedule";
import DriverDashboard from "./pages/driver/DriverDashboard";
import Earnings from "./pages/driver/Earnings";
import RestaurantOrders from "./pages/restaurant/RestaurantOrders";
import MenuManager from "./pages/restaurant/MenuManager";
import RestaurantInfo from "./pages/restaurant/RestaurantInfo";
import Discounts from "./pages/restaurant/Discounts";
import Ratings from "./pages/restaurant/Ratings";
import Complaints from "./pages/restaurant/Complaints";
import SalesReport from "./pages/restaurant/SalesReport";
import Managers from "./pages/restaurant/Managers";
import Deliveries from "./pages/driver/Deliveries";
import Analytics from "./pages/restaurant/Analytics";
import RestaurantVerifications from "./pages/admin/RestaurantVerifications";
import CommercialDriverVerifications from "./pages/admin/CommercialDriverVerifications";
import DeliveryPartnerVerifications from "./pages/admin/DeliveryPartnerVerifications";
import PrivateCarVerifications from "./pages/admin/PrivateCarVerifications";
import DocsPending from "./pages/admin/DocsPending";
import AddressChanges from "./pages/admin/AddressChanges";
import StoriesCms from "./pages/admin/StoriesCms";
import Users from "./pages/admin/Users";
import NotificationsStatus from "./pages/admin/NotificationsStatus";
import AppVersion from "./pages/admin/AppVersion";
import AdminPayouts from "./pages/admin/Payouts";
import OnCallRoster from "./pages/admin/OnCallRoster";
import AdminReports from "./pages/admin/Reports";
import CancellationDisputes from "./pages/admin/CancellationDisputes";
import InternalTasks from "./pages/admin/InternalTasks";
import AdminTeam from "./pages/admin/Team";
import PhotoModeration from "./pages/admin/PhotoModeration";
import LiveOrders from "./pages/admin/LiveOrders";
import PastOrders from "./pages/admin/PastOrders";
import FinancialReport from "./pages/admin/FinancialReport";
import RekycExpiry from "./pages/admin/RekycExpiry";
import NoPartnerReport from "./pages/admin/NoPartnerReport";
import SosAlerts from "./pages/admin/SosAlerts";
import RejectAnalytics from "./pages/restaurant/RejectAnalytics";
import Payouts from "./pages/shared/Payouts";
import ReceiptPage from "./pages/shared/ReceiptPage";
import AuthCallback from "./components/auth/GoogleAuth";

function FullPageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface">
      <div className="flex flex-col items-center gap-3">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-surface-300 border-t-brand" />
        <p className="text-sm text-ink-soft">Loading KohimaTaxi…</p>
      </div>
    </div>
  );
}

// A single verified contact channel (phone OR a real email) is enough — we never
// force a user to verify both. Google/phone-OTP accounts use a synthetic @phone.local email.
const hasContact = (u) => !!(u?.phone || (u?.email && !u.email.endsWith("@phone.local")));

function RequireAuth({ roles, children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/auth/login" state={{ from: location.pathname }} replace />;
  if (!hasContact(user) && user.role !== "admin" && user.role !== "manager" && user.role !== "agent" && !user.is_manager) return <Navigate to="/auth/verify-phone" state={{ from: location.pathname }} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeForRole(user.role)} replace />;
  return children;
}

function RequireUnverified({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/auth/login" replace />;
  if (hasContact(user) || user.role === "admin" || user.role === "manager" || user.role === "agent" || user.is_manager) return <Navigate to={homeForRole(user.role)} replace />;
  return children;
}

function GuestOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (user) return <Navigate to={(hasContact(user) || user.role === "admin" || user.role === "manager" || user.role === "agent" || user.is_manager) ? homeForRole(user.role) : "/auth/verify-phone"} replace />;
  return children;
}

function AppIndex() {
  const { user } = useAuth();
  return <Navigate to={homeForRole(user?.role)} replace />;
}

const CUSTOMER = ["rider", "customer"];

function AppRoutes() {
  // Detect the Google OAuth return synchronously during render (URL fragment), before any guard runs.
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
              {/* Public marketing */}
              <Route element={<MarketingLayout />}>
                <Route path="/" element={<Landing />} />
                <Route path="/for-customers" element={<ForRiders />} />
                <Route path="/for-riders" element={<Navigate to="/for-customers" replace />} />
                <Route path="/for-drivers" element={<ForDrivers />} />
                <Route path="/for-restaurants" element={<ForRestaurants />} />
                <Route path="/about" element={<About />} />
                <Route path="/download" element={<Download />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/stories" element={<Stories />} />
                <Route path="/stories/:slug" element={<StoryDetail />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/refunds" element={<Refunds />} />
                <Route path="/charges" element={<Charges />} />
                <Route path="/partners" element={<Partners />} />
                <Route path="/fares" element={<Partners />} />
                <Route path="/grievance" element={<Grievance />} />
                <Route path="/delete-account" element={<DeleteAccountInfo />} />
              </Route>

              {/* Public shared live trip (no login) */}
              <Route path="/trip/:token" element={<TripShare />} />

              {/* Auth */}
              <Route path="/auth/login" element={<GuestOnly><Login /></GuestOnly>} />
              <Route path="/auth/signup" element={<GuestOnly><Signup /></GuestOnly>} />
              <Route path="/auth/phone" element={<GuestOnly><PhoneAuth /></GuestOnly>} />
              <Route path="/auth/verify-phone" element={<RequireUnverified><VerifyPhone /></RequireUnverified>} />
              <Route path="/auth/signup/verify" element={<GuestOnly><SignupVerify /></GuestOnly>} />
              <Route path="/auth/forgot-password" element={<GuestOnly><ForgotPassword /></GuestOnly>} />

              {/* Logged-in app */}
              <Route path="/app" element={<RequireAuth><AppLayout /></RequireAuth>}>
                <Route index element={<AppIndex />} />
                {/* Customer */}
                <Route path="home" element={<RequireAuth roles={CUSTOMER}><BookRide /></RequireAuth>} />
                <Route path="trip" element={<RequireAuth roles={CUSTOMER}><LiveTrip /></RequireAuth>} />
                <Route path="food" element={<RequireAuth roles={CUSTOMER}><Food /></RequireAuth>} />
                <Route path="food/:restaurantId" element={<RequireAuth roles={CUSTOMER}><RestaurantDetail /></RequireAuth>} />
                <Route path="checkout" element={<RequireAuth roles={CUSTOMER}><Checkout /></RequireAuth>} />
                <Route path="orders" element={<RequireAuth roles={CUSTOMER}><Orders /></RequireAuth>} />
                <Route path="orders/:orderId" element={<RequireAuth roles={[...CUSTOMER, "restaurant", "admin"]}><OrderDetail /></RequireAuth>} />
                <Route path="rides" element={<RequireAuth roles={CUSTOMER}><RideHistory /></RequireAuth>} />
                <Route path="private-cars" element={<RequireAuth roles={CUSTOMER}><PrivateCars /></RequireAuth>} />
                <Route path="goods" element={<RequireAuth roles={CUSTOMER}><GoodsDelivery /></RequireAuth>} />
                <Route path="drivers-near-me" element={<RequireAuth roles={CUSTOMER}><DriversNearMe /></RequireAuth>} />
                <Route path="private-car-jobs" element={<RequireAuth roles={["driver"]}><PrivateCarJobs /></RequireAuth>} />
                {/* Driver */}
                <Route path="driver" element={<RequireAuth roles={["driver"]}><DriverDashboard /></RequireAuth>} />
                <Route path="deliveries" element={<RequireAuth roles={["driver"]}><Deliveries /></RequireAuth>} />
                <Route path="earnings" element={<RequireAuth roles={["driver"]}><Earnings /></RequireAuth>} />
                <Route path="schedule" element={<RequireAuth roles={["driver", ...CUSTOMER]}><Schedule /></RequireAuth>} />
                {/* Restaurant */}
                <Route path="restaurant/orders" element={<RequireAuth roles={["restaurant"]}><RestaurantOrders /></RequireAuth>} />
                <Route path="restaurant/menu" element={<RequireAuth roles={["restaurant"]}><MenuManager /></RequireAuth>} />
                <Route path="restaurant/info" element={<RequireAuth roles={["restaurant"]}><RestaurantInfo /></RequireAuth>} />
                <Route path="restaurant/discounts" element={<RequireAuth roles={["restaurant"]}><Discounts /></RequireAuth>} />
                <Route path="restaurant/ratings" element={<RequireAuth roles={["restaurant"]}><Ratings /></RequireAuth>} />
                <Route path="restaurant/complaints" element={<RequireAuth roles={["restaurant"]}><Complaints /></RequireAuth>} />
                <Route path="restaurant/sales" element={<RequireAuth roles={["restaurant"]}><SalesReport /></RequireAuth>} />
                <Route path="restaurant/managers" element={<RequireAuth roles={["restaurant"]}><Managers /></RequireAuth>} />
                <Route path="restaurant/analytics" element={<RequireAuth roles={["restaurant"]}><Analytics /></RequireAuth>} />
                <Route path="restaurant/reject-reasons" element={<RequireAuth roles={["restaurant"]}><RejectAnalytics /></RequireAuth>} />
                {/* Shared */}
                <Route path="kyc" element={<RequireAuth roles={["driver", "restaurant"]}><KycStatus /></RequireAuth>} />
                <Route path="chat" element={<Chat />} />
                <Route path="chat/:chatId" element={<Chat />} />
                <Route path="profile" element={<Profile />} />
                <Route path="payouts" element={<RequireAuth roles={["driver", "restaurant"]}><Payouts /></RequireAuth>} />
                <Route path="receipts/:kind/:id" element={<ReceiptPage />} />
                {/* Admin */}
                <Route path="admin/verifications" element={<Navigate to="/app/admin/verify/restaurants" replace />} />
                <Route path="admin/verify/restaurants" element={<RequireAuth roles={["admin", "manager"]}><RestaurantVerifications /></RequireAuth>} />
                <Route path="admin/verify/commercial-drivers" element={<RequireAuth roles={["admin", "manager"]}><CommercialDriverVerifications /></RequireAuth>} />
                <Route path="admin/verify/delivery-partners" element={<RequireAuth roles={["admin", "manager"]}><DeliveryPartnerVerifications /></RequireAuth>} />
                <Route path="admin/verify/private-cars" element={<RequireAuth roles={["admin", "manager"]}><PrivateCarVerifications /></RequireAuth>} />
                <Route path="admin/verify/docs-pending" element={<RequireAuth roles={["admin", "manager"]}><DocsPending /></RequireAuth>} />
                <Route path="admin/address-changes" element={<RequireAuth roles={["admin", "manager"]}><AddressChanges /></RequireAuth>} />
                <Route path="admin/payouts" element={<RequireAuth roles={["admin", "manager", "agent"]}><AdminPayouts /></RequireAuth>} />
                <Route path="admin/reports" element={<RequireAuth roles={["admin"]}><AdminReports /></RequireAuth>} />
                <Route path="admin/fee-disputes" element={<RequireAuth roles={["admin", "manager"]}><CancellationDisputes /></RequireAuth>} />
                <Route path="admin/tasks" element={<RequireAuth roles={["admin", "manager", "agent"]}><InternalTasks /></RequireAuth>} />
                <Route path="admin/team" element={<RequireAuth roles={["admin", "manager"]}><AdminTeam /></RequireAuth>} />
                <Route path="admin/permits" element={<Navigate to="/app/admin/verify/commercial-drivers" replace />} />
                <Route path="admin/stories" element={<RequireAuth roles={["admin"]}><StoriesCms /></RequireAuth>} />
                <Route path="admin/users" element={<RequireAuth roles={["admin"]}><Users /></RequireAuth>} />
                <Route path="admin/notifications" element={<RequireAuth roles={["admin"]}><NotificationsStatus /></RequireAuth>} />
                <Route path="admin/app-version" element={<RequireAuth roles={["admin"]}><AppVersion /></RequireAuth>} />
                <Route path="admin/photo-moderation" element={<RequireAuth roles={["admin"]}><PhotoModeration /></RequireAuth>} />
                <Route path="admin/live-orders" element={<RequireAuth roles={["admin", "manager"]}><LiveOrders /></RequireAuth>} />
                <Route path="admin/past-orders" element={<RequireAuth roles={["admin", "manager"]}><PastOrders /></RequireAuth>} />
                <Route path="admin/financial-report" element={<RequireAuth roles={["admin"]}><FinancialReport /></RequireAuth>} />
                <Route path="admin/rekyc" element={<RequireAuth roles={["admin", "manager"]}><RekycExpiry /></RequireAuth>} />
                <Route path="admin/no-partner" element={<RequireAuth roles={["admin", "manager"]}><NoPartnerReport /></RequireAuth>} />
                <Route path="admin/sos" element={<RequireAuth roles={["admin", "manager", "agent"]}><SosAlerts /></RequireAuth>} />
                <Route path="admin/oncall" element={<RequireAuth roles={["admin", "manager", "agent"]}><OnCallRoster /></RequireAuth>} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <CartProvider>
              <LocationProvider>
                <AppRoutes />
                <Toaster position="top-center" richColors closeButton />
              </LocationProvider>
            </CartProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
